import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/apiClient";
import { passwordPolicyError } from "../lib/passwordPolicy";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function Auth() {
  const { login, register, googleLogin } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleButtonRef = useRef(null);

  useEffect(() => {
    if (mode === "forgot" || !GOOGLE_CLIENT_ID) return undefined;

    let cancelled = false;
    let timer;

    const initializeGoogle = () => {
      if (cancelled || !googleButtonRef.current) return;

      if (!window.google?.accounts?.id) {
        timer = window.setTimeout(initializeGoogle, 250);
        return;
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          if (!response?.credential) {
            toast.error("Google oturum bilgisi alınamadı.");
            return;
          }

          setGoogleLoading(true);
          try {
            const result = await googleLogin(response.credential);
            toast.success(
              result?.created
                ? "Google hesabınızla kayıt oluşturuldu."
                : "Google hesabınızla giriş yapıldı."
            );
          } catch (err) {
            toast.error(
              formatError(err.response?.data?.detail) ||
                err.message ||
                "Google ile giriş yapılamadı."
            );
          } finally {
            setGoogleLoading(false);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      googleButtonRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: mode === "register" ? "signup_with" : "signin_with",
        shape: "rectangular",
        width: 352,
        logo_alignment: "left",
      });
    };

    initializeGoogle();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [mode]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register") {
      const policyError = passwordPolicyError(password);
      if (policyError) {
        toast.error(policyError);
        return;
      }
    }
    setLoading(true);

    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Hoş geldiniz!");
      } else if (mode === "register") {
        const result = await register(name, email, password);
        toast.success(result?.message || "Kaydınız alındı. Yönetici onayından sonra giriş yapabilirsiniz.");
        setPassword("");
        setMode("login");
      } else {
        await api.post("/auth/forgot-password", { email });
        toast.success(
          "Eğer bu e-posta kayıtlıysa, sıfırlama bağlantısı gönderildi."
        );
        setMode("login");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    login: "Giriş Yap",
    register: "Kayıt Ol",
    forgot: "Şifremi Unuttum",
  };

  const subs = {
    login: "Hesabınıza erişin",
    register: "Yeni bir hesap oluşturun",
    forgot: "Sıfırlama bağlantısı için e-postanızı girin",
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#09090B] text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />

        <div className="flex items-center gap-2 relative z-10 min-h-[140px]">
          <img
            src="/logo.png"
            alt="OfisGörev"
            className="h-[140px] w-auto object-contain"
          />
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="font-head font-black text-5xl leading-[1.05] tracking-tight mb-6">
            Ekibinizin
            <br />
            tüm görevleri
            <br />
            tek yerde.
          </h1>

          <p className="text-white/60 text-lg leading-relaxed">
            Günlük, haftalık, aylık ve yıllık işlerinizi ekleyin. Dosya
            yükleyin, ekibinizle sohbet edin ve görevlerinizi tek noktadan
            yönetin.
          </p>
        </div>

        <div className="relative z-10 text-white/40 text-sm">
          © 2026 Bestoclass OfisGörev
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex justify-center mb-8">
            <img
              src="/logo.png"
              alt="Bestoclass OfisGörev"
              className="w-[220px] h-auto object-contain"
            />
          </div>

          {mode === "forgot" && (
            <button
              type="button"
              onClick={() => setMode("login")}
              className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground"
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-4 h-4" /> Girişe dön
            </button>
          )}

          <h2
            className="font-head font-bold text-3xl tracking-tight mb-1"
            data-testid="auth-title"
          >
            {titles[mode]}
          </h2>

          <p className="text-muted-foreground mb-8">{subs[mode]}</p>

          {mode !== "forgot" && (
            <>
              {GOOGLE_CLIENT_ID ? (
                <div className="relative min-h-[44px] flex items-center justify-center">
                  <div
                    ref={googleButtonRef}
                    className={googleLoading ? "pointer-events-none opacity-60" : ""}
                  />

                  {googleLoading && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-md bg-background/70">
                      <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                  Google Client ID frontend ortam değişkeninde tanımlı değil.
                </div>
              )}

              <div className="my-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">veya</span>
                <div className="h-px flex-1 bg-border" />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-2">
                <Label>Ad Soyad</Label>
                <Input
                  data-testid="name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Adınız"
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>E-posta</Label>
              <Input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@sirket.com"
                required
              />
            </div>

            {mode !== "forgot" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Şifre</Label>

                  {mode === "login" && (
                    <button
                      type="button"
                      onClick={() => setMode("forgot")}
                      className="text-xs text-primary hover:underline"
                      data-testid="forgot-link"
                    >
                      Şifremi unuttum?
                    </button>
                  )}
                </div>

                <Input
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  minLength={mode === "register" ? 12 : undefined}
                  maxLength={128}
                  required
                />
                {mode === "register" && (
                  <p className="text-xs text-muted-foreground" data-testid="password-policy-hint">
                    En az 12 karakter; büyük/küçük harf, rakam ve sembol gruplarından en az üçünü kullanın.
                  </p>
                )}
              </div>
            )}

            <Button
              data-testid="submit-btn"
              type="submit"
              disabled={loading || googleLoading}
              className="w-full h-11 font-medium"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === "login" ? (
                "Giriş Yap"
              ) : mode === "register" ? (
                "Kayıt Ol"
              ) : (
                "Bağlantı Gönder"
              )}
            </Button>
          </form>

          {mode !== "forgot" && (
            <p className="text-center text-sm text-muted-foreground mt-6">
              {mode === "login"
                ? "Hesabınız yok mu? "
                : "Zaten hesabınız var mı? "}

              <button
                type="button"
                data-testid="toggle-mode"
                onClick={() =>
                  setMode(mode === "login" ? "register" : "login")
                }
                className="text-primary font-medium hover:underline"
              >
                {mode === "login" ? "Kayıt olun" : "Giriş yapın"}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
